import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useMemo, useCallback, useEffect } from "react";

interface SearchFilters {
  query: string;
  categories: string[];
  brands: string[];
  minPrice?: number;
  maxPrice?: number;
  inStock: boolean;
  sortBy: "relevance" | "price_low" | "price_high" | "newest" | "name";
}

interface SearchResult {
  id: string;
  name: string;
  slug: string;
  price: number;
  compare_at_price: number | null;
  images: string[];
  short_description: string | null;
  category_id: string | null;
  brand_id: string | null;
  stock_quantity: number;
  is_active: boolean;
  is_featured: boolean;
  created_at: string;
  categories?: { name: string; slug: string } | null;
  brands?: { name: string } | null;
}

interface SearchSuggestion {
  type: "product" | "category" | "brand";
  id: string;
  name: string;
  slug?: string;
  image?: string;
}

const defaultFilters: SearchFilters = {
  query: "",
  categories: [],
  brands: [],
  minPrice: undefined,
  maxPrice: undefined,
  inStock: false,
  sortBy: "relevance",
};

// Custom debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

export function useAdvancedSearch() {
  const [filters, setFilters] = useState<SearchFilters>(defaultFilters);
  const debouncedQuery = useDebounce(filters.query, 300);

  // --- NLP Lite Intent Parser ---
  const { parsedQuery, extractedMinPrice, extractedMaxPrice } = useMemo(() => {
    let q = debouncedQuery.toLowerCase();
    let maxP = filters.maxPrice;
    let minP = filters.minPrice;

    const underRegex = /(?:under|below|less than|budget of)\s*(?:rs|inr|₹|rs\.)?\s*(\d+)/i;
    const underMatch = q.match(underRegex);
    if (underMatch && underMatch[1]) {
      maxP = parseInt(underMatch[1], 10);
      q = q.replace(underRegex, '').trim();
    }

    const overRegex = /(?:over|above|more than)\s*(?:rs|inr|₹|rs\.)?\s*(\d+)/i;
    const overMatch = q.match(overRegex);
    if (overMatch && overMatch[1]) {
      minP = parseInt(overMatch[1], 10);
      q = q.replace(overRegex, '').trim();
    }

    q = q.replace(/\s+/g, ' ').trim();

    return { parsedQuery: q, extractedMinPrice: minP, extractedMaxPrice: maxP };
  }, [debouncedQuery, filters.maxPrice, filters.minPrice]);

  // Main search query
  const { data: results, isLoading: searching } = useQuery({
    queryKey: ["advanced-search", debouncedQuery, filters.categories, filters.brands, filters.minPrice, filters.maxPrice, filters.inStock, filters.sortBy],
    queryFn: async () => {
      let query = supabase
        .from("products")
        .select("*, categories(name, slug), brands(name)")
        .eq("is_active", true);

      // Text search
      if (parsedQuery && parsedQuery.length >= 2) {
        // Use ilike for partial matching
        query = query.or(`name.ilike.%${parsedQuery}%,short_description.ilike.%${parsedQuery}%,description.ilike.%${parsedQuery}%`);
      }

      // Category filter
      if (filters.categories.length > 0) {
        query = query.in("category_id", filters.categories);
      }

      // Brand filter
      if (filters.brands.length > 0) {
        query = query.in("brand_id", filters.brands);
      }

      // Price range (using NLP extracted bounds or explicit filters)
      if (extractedMinPrice !== undefined) {
        query = query.gte("price", extractedMinPrice);
      }
      if (extractedMaxPrice !== undefined) {
        query = query.lte("price", extractedMaxPrice);
      }

      // In stock filter
      if (filters.inStock) {
        query = query.gt("stock_quantity", 0);
      }

      // Sorting
      switch (filters.sortBy) {
        case "price_low":
          query = query.order("price", { ascending: true });
          break;
        case "price_high":
          query = query.order("price", { ascending: false });
          break;
        case "newest":
          query = query.order("created_at", { ascending: false });
          break;
        case "name":
          query = query.order("name", { ascending: true });
          break;
        default:
          // Relevance - featured first, then by name
          query = query.order("is_featured", { ascending: false }).order("name");
      }

      query = query.limit(50);

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as SearchResult[];
    },
    enabled: debouncedQuery.length >= 2 || filters.categories.length > 0 || filters.brands.length > 0,
  });

  // Autocomplete suggestions
  const { data: suggestions } = useQuery({
    queryKey: ["search-suggestions", debouncedQuery],
    queryFn: async () => {
      if (debouncedQuery.length < 2) return [];

      const suggestions: SearchSuggestion[] = [];

      // Product suggestions
      const { data: products } = await supabase
        .from("products")
        .select("id, name, slug, images")
        .eq("is_active", true)
        .ilike("name", `%${parsedQuery}%`)
        .limit(5);

      products?.forEach(p => {
        suggestions.push({
          type: "product",
          id: p.id,
          name: p.name,
          slug: p.slug,
          image: p.images?.[0],
        });
      });

      // Category suggestions
      const { data: categories } = await supabase
        .from("categories")
        .select("id, name, slug")
        .ilike("name", `%${parsedQuery}%`)
        .limit(3);

      categories?.forEach(c => {
        suggestions.push({
          type: "category",
          id: c.id,
          name: c.name,
          slug: c.slug,
        });
      });

      // Brand suggestions
      const { data: brands } = await supabase
        .from("brands")
        .select("id, name")
        .ilike("name", `%${parsedQuery}%`)
        .limit(3);

      brands?.forEach(b => {
        suggestions.push({
          type: "brand",
          id: b.id,
          name: b.name,
        });
      });

      return suggestions;
    },
    enabled: debouncedQuery.length >= 2,
  });

  // Filter options
  const { data: filterOptions } = useQuery({
    queryKey: ["search-filter-options"],
    queryFn: async () => {
      const [categoriesRes, brandsRes, priceRes] = await Promise.all([
        supabase.from("categories").select("id, name").order("name"),
        supabase.from("brands").select("id, name").order("name"),
        supabase.from("products").select("price").eq("is_active", true),
      ]);

      const prices = priceRes.data?.map(p => p.price) || [0];
      const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
      const maxPrice = prices.length > 0 ? Math.max(...prices) : 10000;

      return {
        categories: categoriesRes.data || [],
        brands: brandsRes.data || [],
        priceRange: { min: minPrice, max: maxPrice },
      };
    },
  });

  const updateFilters = useCallback((updates: Partial<SearchFilters>) => {
    setFilters(prev => ({ ...prev, ...updates }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.categories.length > 0) count++;
    if (filters.brands.length > 0) count++;
    if (filters.minPrice !== undefined || filters.maxPrice !== undefined) count++;
    if (filters.inStock) count++;
    return count;
  }, [filters]);

  return {
    filters,
    results: results || [],
    suggestions: suggestions || [],
    filterOptions,
    searching,
    updateFilters,
    resetFilters,
    activeFilterCount,
  };
}
